import supabase from '../utils/supabase.js';
import messagingSecurity from '../utils/messagingSecurity';
import realtimeRelationshipService from './realtimeRelationshipService.js';
import realtimeConnectionManager from '../utils/realtimeConnectionManager.js';
import ErrorHandler from '../utils/errorHandler';
import cacheService from './cacheService.js';

/**
 * Secure messaging service for VA_Sprouts
 * Handles all messaging functionality between VAs and employers with security controls
 */
class MessagingService {
  constructor() {
    // Relationship cache for performance optimization
    this.relationshipCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache timeout
    this.maxCacheSize = 1000; // Maximum number of cached relationships
    
    // Cache cleanup interval
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredCache();
    }, 60 * 1000); // Run cleanup every minute

    // Initialize real-time relationship updates (async)
    this.initializeRealtimeUpdates().catch(error => {
      ErrorHandler.logError('MessagingService', 'Failed to initialize realtime updates', { error });
    });
  }

  /**
   * Initialize real-time relationship updates
   */
  async initializeRealtimeUpdates() {
    try {
      // Initialize the centralized connection manager first
      await realtimeConnectionManager.initialize();
      
      // Then initialize the real-time service
      realtimeRelationshipService.initialize();

      // Register event handler for relationship changes
      this.realtimeHandlerId = realtimeRelationshipService.onRelationshipChange(
        'relationship_changed',
        (data) => {
          this.handleRealtimeRelationshipChange(data);
        }
      );

      ErrorHandler.logInfo('MessagingService', 'Real-time relationship updates initialized');
    } catch (error) {
      ErrorHandler.logError('MessagingService', 'Error initializing real-time updates', { error });
      // Don't fail the entire service if realtime fails
      ErrorHandler.logInfo('MessagingService', 'Continuing without real-time updates');
    }
  }

  /**
   * Handle real-time relationship changes
   * @param {Object} data - The relationship change data
   */
  handleRealtimeRelationshipChange(data) {
    const { vaId, employerId, type, action } = data;
    
    // Invalidate cache for this specific relationship
    this.invalidateRelationshipCache(vaId, employerId);
    
    ErrorHandler.logInfo('MessagingService', `Real-time cache invalidation: ${type} ${action}`, { vaId, employerId });
  }

  /**
   * Generate cache key for VA-employer relationship
   * @param {string} vaId - The ID of the VA
   * @param {string} employerId - The ID of the employer
   * @returns {string} - Cache key
   */
  getCacheKey(vaId, employerId) {
    return `${vaId}:${employerId}`;
  }

  /**
   * Get cached relationship status
   * @param {string} vaId - The ID of the VA
   * @param {string} employerId - The ID of the employer
   * @returns {boolean|null} - Cached result or null if not cached/expired
   */
  getCachedRelationship(vaId, employerId) {
    const key = this.getCacheKey(vaId, employerId);
    const cached = this.relationshipCache.get(key);
    
    if (!cached) {
      return null;
    }
    
    // Check if cache entry is expired
    if (Date.now() - cached.timestamp > this.cacheTimeout) {
      this.relationshipCache.delete(key);
      return null;
    }
    
    return cached.hasRelationship;
  }

  /**
   * Cache relationship status
   * @param {string} vaId - The ID of the VA
   * @param {string} employerId - The ID of the employer
   * @param {boolean} hasRelationship - Whether relationship exists
   */
  setCachedRelationship(vaId, employerId, hasRelationship) {
    // Prevent cache from growing too large
    if (this.relationshipCache.size >= this.maxCacheSize) {
      // Remove oldest entry
      const firstKey = this.relationshipCache.keys().next().value;
      this.relationshipCache.delete(firstKey);
    }
    
    const key = this.getCacheKey(vaId, employerId);
    this.relationshipCache.set(key, {
      hasRelationship,
      timestamp: Date.now()
    });
  }

  /**
   * Clean up expired cache entries
   */
  cleanupExpiredCache() {
    const now = Date.now();
    for (const [key, value] of this.relationshipCache.entries()) {
      if (now - value.timestamp > this.cacheTimeout) {
        this.relationshipCache.delete(key);
      }
    }
  }

  /**
   * Invalidate cached relationship for specific VA-employer pair
   * @param {string} vaId - The ID of the VA
   * @param {string} employerId - The ID of the employer
   */
  invalidateRelationshipCache(vaId, employerId) {
    const key = this.getCacheKey(vaId, employerId);
    this.relationshipCache.delete(key);
  }

  /**
   * Clear all cached relationships
   */
  clearRelationshipCache() {
    this.relationshipCache.clear();
  }

  /**
   * Get cache statistics for monitoring
   * @returns {Object} - Cache statistics
   */
  getCacheStats() {
    const now = Date.now();
    let expiredCount = 0;
    
    for (const value of this.relationshipCache.values()) {
      if (now - value.timestamp > this.cacheTimeout) {
        expiredCount++;
      }
    }
    
    return {
      totalEntries: this.relationshipCache.size,
      expiredEntries: expiredCount,
      activeEntries: this.relationshipCache.size - expiredCount,
      cacheHitRatio: this.cacheHitRatio || 0,
      maxSize: this.maxCacheSize,
      timeout: this.cacheTimeout
    };
  }

  /**
   * Get all conversations for the current user
   * @param {string} userId - The ID of the current user
   * @returns {Promise<Object>} - Result object with success, data, and error
   */
  async getConversations(userId) {
    if (!userId) {
      return { success: false, error: 'No user ID provided', data: [] };
    }
    
    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId)) {
      return { success: false, error: 'Invalid user ID format', data: [] };
    }

    try {
      // Try to get from cache first
      const cachedConversations = await cacheService.getConversations(userId);
      if (cachedConversations) {
        ErrorHandler.logInfo('MessagingService', 'Conversations loaded from cache', { userId });
        return { success: true, data: cachedConversations, error: null, fromCache: true };
      }

      // Call the stored function to get user conversations
      const { data: functionData, error: functionError } = await supabase.rpc('get_user_conversations', {
        user_id_param: userId
      });

      if (functionError) {
        // Error logged via ErrorHandler
        return { success: false, error: `Failed to fetch conversations: ${functionError.message}`, data: [] };
      }

      // Transform the data to match expected format
      const conversations = (functionData || []).map(conv => {
        return {
          id: conv.conversation_id,
          recipient_id: conv.participant_1 === userId ? conv.participant_2 : conv.participant_1,
          recipient_name: conv.participant_name,
          recipient_role: conv.participant_role || 'va',
          recipient_avatar: conv.participant_avatar,
          company_name: conv.company_name,
          last_message: {
            content: conv.last_message || 'Start a conversation...',
            timestamp: conv.last_message_at ? new Date(conv.last_message_at) : new Date(),
            sender_id: conv.last_sender_id
          },
          unread_count: parseInt(conv.unread_count) || 0,
          job_title: conv.job_title || '',
          job_context: conv.job_context || ''
        };
      });

      // Cache the conversations (2 minute TTL)
      await cacheService.cacheConversations(userId, conversations, 120);
      ErrorHandler.logInfo('MessagingService', 'Conversations cached', { userId, count: conversations.length });
      
      return { success: true, data: conversations, error: null, fromCache: false };

    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get messages for a specific conversation
   * @param {string} conversationId - The ID of the conversation
   * @param {string} userId - Optional user ID for logging
   * @returns {Promise<Object>} - Result object with success, data, and error
   */
  async getMessages(conversationId, userId = null) {
    if (!conversationId) {
      return { success: false, error: 'No conversation ID provided', data: [] };
    }

    // Ensure conversationId is a string
    const stringConversationId = String(conversationId);
    
    // Additional validation for UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(stringConversationId)) {
      ErrorHandler.logError('Invalid UUID format for conversation ID in MessagingService', null, { conversationId: stringConversationId });
      return { success: false, error: 'Invalid conversation ID format', data: [] };
    }

    try {
      // Try to get from cache first
      const cachedMessages = await cacheService.getMessages(stringConversationId);
      if (cachedMessages) {
        ErrorHandler.logInfo('MessagingService', 'Messages loaded from cache', { conversationId: stringConversationId });
        return { success: true, data: cachedMessages, error: null, fromCache: true };
      }

      const { data, error } = await supabase
        .from('messages')
        .select(`
          id,
          conversation_id,
          sender_id,
          content,
          message_type,
          attachments,
          is_read,
          sent_at,
          user_profiles:sender_id (
            full_name,
            profile_picture
          )
        `)
        .eq('conversation_id', stringConversationId)
        .order('sent_at', { ascending: true });
      
      if (error) {
        messagingSecurity.logSecurityEvent('message_fetch_failed', { 
          conversationId, 
          userId, 
          error: error.message 
        });
        return { success: false, error: error.message, data: [] };
      }
      
      // Log message access for security monitoring
      if (userId) {
        messagingSecurity.logSecurityEvent('messages_accessed', { 
          conversationId, 
          userId, 
          messageCount: data?.length || 0 
        });
      }
      
      // Transform to include sender name for display
      const transformedMessages = (data || []).map(message => ({
        ...message,
        sender_name: message.user_profiles?.full_name || 'Unknown User',
        created_at: message.sent_at // Maintain backward compatibility
      }));

      // Cache the messages (2 minute TTL)
      await cacheService.cacheMessages(stringConversationId, transformedMessages, 120);
      ErrorHandler.logInfo('MessagingService', 'Messages cached', { conversationId: stringConversationId, count: transformedMessages.length });

      return { success: true, data: transformedMessages, error: null, fromCache: false };

    } catch (error) {
      messagingSecurity.logSecurityEvent('message_fetch_exception', { 
        conversationId, 
        userId, 
        error: error.message 
      });
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Send a new message in a conversation
   * @param {string} conversationId - The ID of the conversation
   * @param {string} senderId - The ID of the message sender
   * @param {string} content - The message content
   * @param {Array} attachments - Array of attachment objects
   * @returns {Promise<Object>} - Result object with success, data, and error
   */
  async sendMessage(conversationId, senderId, content, attachments = []) {
    // Ensure conversationId is a string
    const stringConversationId = String(conversationId);
    
    // Security validation
    if (!messagingSecurity.validateUserPermissions({ id: senderId }, 'send')) {
      messagingSecurity.logSecurityEvent('unauthorized_send_attempt', { senderId, conversationId });
      return { success: false, error: 'User not authorized to send messages', data: null };
    }

    if (!messagingSecurity.checkRateLimit(senderId)) {
      messagingSecurity.logSecurityEvent('rate_limit_exceeded', { senderId, conversationId });
      return { success: false, error: 'Rate limit exceeded. Please wait before sending more messages.', data: null };
    }

    // Sanitize and validate message data
    const sanitizedContent = messagingSecurity.sanitizeMessageContent(content);
    const messageData = {
      conversation_id: stringConversationId,
      sender_id: senderId,
      content: sanitizedContent,
      message_type: 'text',
      attachments: attachments || []
    };

    const validation = messagingSecurity.validateMessageData(messageData);
    if (!validation.isValid) {
      messagingSecurity.logSecurityEvent('invalid_message_data', { senderId, errors: validation.errors });
      return { success: false, error: `Invalid message data: ${validation.errors.join(', ')}`, data: null };
    }

    try {
      // Check current user authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        messagingSecurity.logSecurityEvent('auth_check_failed', { senderId, conversationId, error: authError?.message });
        return { success: false, error: 'Authentication required to send messages', data: null };
      }

      // Verify the senderId matches the authenticated user
      if (user.id !== senderId) {
        messagingSecurity.logSecurityEvent('sender_id_mismatch', { 
          senderId, 
          conversationId, 
          authenticatedUserId: user.id 
        });
        return { success: false, error: 'Sender ID does not match authenticated user', data: null };
      }

      // First get the conversation to determine recipient
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .select('participant_1, participant_2')
        .eq('id', stringConversationId)
        .single();
      
      if (convError) {
        messagingSecurity.logSecurityEvent('conversation_fetch_failed', { 
          senderId, 
          conversationId, 
          error: convError.message,
          errorCode: convError.code,
          errorDetails: convError.details,
          hint: convError.hint,
          currentUser: user?.id
        });
        
        // If conversation doesn't exist, provide a more helpful error message
        if (convError.code === 'PGRST116') {
          return { success: false, error: 'Conversation not found. Please create a conversation first.', data: null };
        }
        
        // Handle 406 Not Acceptable errors (likely RLS policy issues)
        if (convError.code === '406' || convError.message?.includes('Not Acceptable')) {
          ErrorHandler.logError('MessagingService 406 Not Acceptable error - likely RLS policy rejection', convError, {
            conversationId,
            userId: user?.id
          });
          return { success: false, error: 'Access denied to conversation. Please check permissions.', data: null };
        }
        
        return { success: false, error: `Failed to fetch conversation details: ${convError.message}`, data: null };
      }
      
      if (!conversation) {
        messagingSecurity.logSecurityEvent('conversation_not_found', { senderId, conversationId });
        return { success: false, error: 'Conversation not found', data: null };
      }
      
      // Verify the current user is a participant in this conversation
      const isParticipant = conversation.participant_1 === user.id || conversation.participant_2 === user.id;
      if (!isParticipant) {
        messagingSecurity.logSecurityEvent('unauthorized_conversation_access', { 
          senderId, 
          conversationId, 
          currentUser: user.id,
          participants: [conversation.participant_1, conversation.participant_2]
        });
        return { success: false, error: 'Access denied: You are not a participant in this conversation', data: null };
      }
      
      // Determine recipient ID
      const recipientId = conversation.participant_1 === senderId 
        ? conversation.participant_2 
        : conversation.participant_1;
      
      if (!recipientId) {
        messagingSecurity.logSecurityEvent('recipient_determination_failed', { senderId, conversationId, conversation });
        return { success: false, error: 'Could not determine message recipient', data: null };
      }
      
      // Add recipient_id to message data
      messageData.recipient_id = recipientId;
      messageData.sent_at = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('messages')
        .insert(messageData)
        .select(`
          id,
          conversation_id,
          sender_id,
          content,
          message_type,
          attachments,
          sent_at,
          user_profiles:sender_id (
            full_name,
            profile_picture
          )
        `)
        .single();
      
      if (error) {
        messagingSecurity.logSecurityEvent('message_send_failed', { senderId, error: error.message });
        return { success: false, error: `Failed to send message: ${error.message}`, data: null };
      }

      // Update conversation's last_message_at timestamp
      await supabase
        .from('conversations')
        .update({ last_message_at: data.sent_at })
        .eq('id', conversationId);

      // Invalidate caches for both participants
      await Promise.all([
        cacheService.invalidateConversations(senderId),
        cacheService.invalidateConversations(recipientId),
        cacheService.invalidateMessages(stringConversationId)
      ]);

      // Increment unread count for recipient
      await cacheService.incrementUnreadCount(recipientId);
      
      // Log successful message send
      messagingSecurity.logSecurityEvent('message_sent', { 
        senderId, 
        conversationId, 
        messageId: data.id 
      });

      ErrorHandler.logInfo('MessagingService', 'Caches invalidated after message send', { 
        senderId, 
        recipientId, 
        conversationId 
      });
      
      // Transform for consistency
      const transformedMessage = {
        ...data,
        sender_name: data.user_profiles?.full_name || 'Unknown User',
        created_at: data.sent_at // Maintain backward compatibility
      };

      return { success: true, data: transformedMessage, error: null };

    } catch (error) {
      messagingSecurity.logSecurityEvent('message_send_exception', { 
        senderId, 
        conversationId, 
        error: error.message 
      });
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Mark messages as read in a conversation
   * @param {string} conversationId - The ID of the conversation
   * @param {string} userId - The ID of the user marking messages as read
   * @returns {Promise<boolean>} - Success status
   */
  async markMessagesAsRead(conversationId, userId) {
    if (!messagingSecurity.validateUserPermissions({ id: userId }, 'mark_read')) {
      messagingSecurity.logSecurityEvent('unauthorized_mark_read_attempt', { userId, conversationId });
      return false;
    }

    try {
      // Try using the stored function first
      const { error: functionError } = await supabase.rpc('mark_messages_as_read', {
        conversation_uuid: conversationId,
        reader_id: userId
      });
      
      if (!functionError) {
        // Reset unread count and invalidate caches
        await Promise.all([
          cacheService.resetUnreadCount(userId),
          cacheService.invalidateConversations(userId),
          cacheService.invalidateMessages(conversationId)
        ]);

        messagingSecurity.logSecurityEvent('messages_marked_read', { userId, conversationId });
        ErrorHandler.logInfo('MessagingService', 'Messages marked as read, caches invalidated', { userId, conversationId });
        return true;
      }

      // Fallback to direct update if function not available
      ErrorHandler.logWarning('MessagingService mark_messages_as_read function failed, using fallback', functionError);

      // Ensure conversationId is a string
      const stringConversationId = String(conversationId);
      
      const { error: updateError } = await supabase
        .from('messages')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('conversation_id', stringConversationId)
        .neq('sender_id', userId)
        .eq('is_read', false);
      
      if (updateError) {
        ErrorHandler.logError('MessagingService direct update failed', updateError);
        messagingSecurity.logSecurityEvent('mark_read_failed', { 
          userId, 
          conversationId, 
          error: updateError.message 
        });
        return false;
      }

      // Reset unread count and invalidate caches for fallback path too
      await Promise.all([
        cacheService.resetUnreadCount(userId),
        cacheService.invalidateConversations(userId),
        cacheService.invalidateMessages(conversationId)
      ]);
      
      messagingSecurity.logSecurityEvent('messages_marked_read_direct', { userId, conversationId });
      ErrorHandler.logInfo('MessagingService', 'Messages marked as read (fallback), caches invalidated', { userId, conversationId });
      return true;
    } catch (error) {
      ErrorHandler.logError('MessagingService markMessagesAsRead exception', error);
      messagingSecurity.logSecurityEvent('mark_read_exception', { 
        userId, 
        conversationId, 
        error: error.message 
      });
      return false;
    }
  }

  /**
   * Check if a VA has a relationship with an employer (employment or job application)
   * Uses caching for improved performance
   * @param {string} vaId - The ID of the VA
   * @param {string} employerId - The ID of the employer
   * @returns {Promise<boolean>} - True if relationship exists, false otherwise
   */
  async checkVAEmployerRelationship(vaId, employerId) {
    // Check cache first
    const cached = this.getCachedRelationship(vaId, employerId);
    if (cached !== null) {
      // Update cache hit ratio for monitoring
      this.cacheHitRatio = ((this.cacheHitRatio || 0) * 0.9) + (1 * 0.1);
      return cached;
    }

    // Update cache miss ratio for monitoring
    this.cacheHitRatio = ((this.cacheHitRatio || 0) * 0.9) + (0 * 0.1);

    try {
      let hasRelationship = false;

      // First, get all jobs from this employer
      const { data: employerJobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id')
        .eq('employer_id', employerId);

      if (jobsError) {
        ErrorHandler.logError('Error fetching employer jobs in MessagingService', jobsError);
        // Don't cache errors
        return false;
      }

      if (employerJobs && employerJobs.length > 0) {
        const jobIds = employerJobs.map(job => job.id);
        
        // Check if VA has applied to any of employer's jobs
        const { data: applications, error: appError } = await supabase
          .from('job_applications')
          .select('id')
          .eq('applicant_id', vaId)
          .in('job_id', jobIds)
          .limit(1);

        if (appError) {
          ErrorHandler.logError('Error checking job applications in MessagingService', appError);
        } else if (applications && applications.length > 0) {
          hasRelationship = true;
        }
      }

      // Only check contracts if no job application relationship found
      if (!hasRelationship) {
        // Check if VA has been hired by employer (contracts table)
        const { data: contracts, error: contractError } = await supabase
          .from('contracts')
          .select('id')
          .eq('va_id', vaId)
          .eq('employer_id', employerId)
          .limit(1);

        if (contractError) {
          ErrorHandler.logError('Error checking contracts in MessagingService', contractError);
        } else if (contracts && contracts.length > 0) {
          hasRelationship = true;
        }
      }

      // Cache the result
      this.setCachedRelationship(vaId, employerId, hasRelationship);
      
      return hasRelationship;
    } catch (error) {
      ErrorHandler.logError('Error checking VA-employer relationship in MessagingService', error);
      // Don't cache errors
      return false;
    }
  }

  /**
   * Create a new conversation or get existing one between two users
   * @param {string} userId - The ID of the current user
   * @param {string} recipientId - The ID of the recipient
   * @param {string} jobId - Optional job ID for context
   * @returns {Promise<Object>} - Result object with success, data, and error
   */
  async createOrGetConversation(userId, recipientId, jobId = null) {
    // Security validation
    if (!messagingSecurity.validateUserPermissions({ id: userId }, 'create_conversation')) {
      messagingSecurity.logSecurityEvent('unauthorized_conversation_creation', { userId, recipientId });
      return { success: false, error: 'User not authorized to create conversations', data: null };
    }

    if (!messagingSecurity.validateRecipient(userId, recipientId)) {
      messagingSecurity.logSecurityEvent('invalid_recipient', { userId, recipientId });
      return { success: false, error: 'Invalid recipient for conversation', data: null };
    }

    try {
      // Get user profiles to determine roles and validate relationships
      const { data: userProfile, error: userError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .single();
        
      const { data: recipientProfile, error: recipientError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', recipientId)
        .single();

      if (userError || recipientError) {
        messagingSecurity.logSecurityEvent('profile_fetch_failed', { userId, recipientId });
        return { success: false, error: 'Failed to validate user profiles', data: null };
      }

      // If VA is trying to contact employer, check for relationship
      if (userProfile.role === 'va' && recipientProfile.role === 'employer') {
        const hasRelationship = await this.checkVAEmployerRelationship(userId, recipientId);
        if (!hasRelationship) {
          messagingSecurity.logSecurityEvent('unauthorized_va_contact_attempt', { 
            vaId: userId, 
            employerId: recipientId 
          });
          return { 
            success: false, 
            error: 'You can only message employers you have applied to or worked with', 
            data: null 
          };
        }
      }
      // Try using the improved stored function with automatic merging
      const { data: functionData, error: functionError } = await supabase.rpc('get_or_create_merged_conversation', { 
        user1_id: userId,
        user2_id: recipientId,
        p_job_id: jobId
      });
      
      if (!functionError && functionData) {
        // The RPC function returns an object with conversation_id, created, and merged fields
        const result = Array.isArray(functionData) && functionData.length > 0 
          ? functionData[0] 
          : functionData;
          
        const conversationId = result.conversation_id;
        const wasCreated = result.created;
        const wasMerged = result.merged;
          
        if (!conversationId) {
          ErrorHandler.logError('No conversation ID returned from RPC function in MessagingService', null, { functionData });
          throw new Error('Failed to get conversation ID from database function');
        }
        
        // Log successful conversation creation/retrieval with merge info
        messagingSecurity.logSecurityEvent('conversation_accessed', { 
          userId, 
          recipientId, 
          conversationId: conversationId,
          jobId,
          wasCreated,
          wasMerged
        });
        
        // If conversations were merged, log this important event and invalidate caches
        if (wasMerged) {
          ErrorHandler.logInfo(`Conversations merged for users ${userId} ↔ ${recipientId}`);
          messagingSecurity.logSecurityEvent('conversations_merged', { 
            userId, 
            recipientId, 
            conversationId: conversationId,
            jobId 
          });

          // Invalidate conversation caches for both users
          await Promise.all([
            cacheService.invalidateConversations(userId),
            cacheService.invalidateConversations(recipientId)
          ]);
        }
        
        const conversationData = {
          id: conversationId,
          participant_1: userId,
          participant_2: recipientId,
          job_id: jobId,
          created_at: new Date().toISOString(),
          was_merged: wasMerged
        };

        return { success: true, data: conversationData, error: null };
      }

      // Fallback to manual conversation creation/retrieval with improved deduplication
      
      // First, try to find existing conversation between these users (any job_id)
      // Prioritize conversations with messages, then most recent activity
      let query = supabase
        .from('conversations')
        .select('id, created_at, last_message_at, job_id, (select count(*) from messages where conversation_id = conversations.id) as message_count');
      
      // Build the query to find conversations between these two users
      query = query.or(`and(participant_1.eq.${userId},participant_2.eq.${recipientId}),and(participant_1.eq.${recipientId},participant_2.eq.${userId})`);
      
      // Order by: conversations with messages first, then most recent activity, then oldest creation
      query = query.order('message_count', { ascending: false });
      query = query.order('last_message_at', { ascending: false, nullsFirst: false });
      query = query.order('created_at', { ascending: true });
      
      const { data: existingConvs, error: findError } = await query;

      if (!findError && existingConvs && existingConvs.length > 0) {
        // Use the first (best) conversation found
        const existingConv = existingConvs[0];
        
        // Note: With unified conversations, multiple rows should not occur
        // but log if they do for monitoring purposes
        if (existingConvs.length > 1) {
          ErrorHandler.logWarning(`Found multiple conversations for users ${userId} ↔ ${recipientId}. This should not occur with unified conversation system.`);
          messagingSecurity.logSecurityEvent('unexpected_multiple_conversations', { 
            userId, 
            recipientId, 
            conversationCount: existingConvs.length,
            selectedConversationId: existingConv.id,
            note: 'Unified conversation system should prevent duplicates'
          });
        }
        
        // Conversation exists
        messagingSecurity.logSecurityEvent('existing_conversation_accessed', { 
          userId, 
          recipientId, 
          conversationId: existingConv.id,
          jobId 
        });

        const conversationData = {
          id: existingConv.id,
          participant_1: userId,
          participant_2: recipientId,
          job_id: existingConv.job_id, // Use existing job_id, jobId param is optional context
          created_at: new Date().toISOString()
        };

        // If jobId provided and conversation doesn't have one, update it
        if (jobId && !existingConv.job_id) {
          const { data: updated } = await supabase
            .from('conversations')
            .update({ job_id: jobId })
            .eq('id', existingConv.id)
            .select()
            .single();
          
          if (updated) {
            conversationData.job_id = jobId;
          }
        }

        return { success: true, data: conversationData, error: null };
      }

      // Create new conversation with consistent participant ordering
      const participant1 = userId < recipientId ? userId : recipientId;
      const participant2 = userId < recipientId ? recipientId : userId;
      
      const { data: newConv, error: createError } = await supabase
        .from('conversations')
        .insert({
          participant_1: participant1,
          participant_2: participant2,
          job_id: jobId
        })
        .select('id')
        .single();

      if (createError) {
        messagingSecurity.logSecurityEvent('conversation_creation_failed', { 
          userId, 
          recipientId, 
          error: createError.message 
        });
        return { success: false, error: `Failed to create conversation: ${createError.message}`, data: null };
      }

      // Log successful conversation creation
      messagingSecurity.logSecurityEvent('new_conversation_created', { 
        userId, 
        recipientId, 
        conversationId: newConv.id,
        jobId 
      });
      
      const conversationData = {
        id: newConv.id,
        participant_1: userId,
        participant_2: recipientId,
        job_id: jobId,
        created_at: new Date().toISOString()
      };

      return { success: true, data: conversationData, error: null };

    } catch (error) {
      messagingSecurity.logSecurityEvent('conversation_creation_exception', { 
        userId, 
        recipientId, 
        error: error.message 
      });
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Mark messages as read
   * @param {string} conversationId - The ID of the conversation
   * @param {string} userId - The ID of the current user
   * @returns {Promise<Object>} - Result object with success, data, and error
   */
  async markAsRead(conversationId, userId) {
    try {
      const success = await this.markMessagesAsRead(conversationId, userId);
      return { success, data: null, error: success ? null : 'Failed to mark messages as read' };
    } catch (error) {
      ErrorHandler.logError('MessagingService markAsRead error', error);
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Get unread message count for a user (from Redis cache)
   * @param {string} userId - The ID of the user
   * @returns {Promise<number>} - The unread count
   */
  async getUnreadCount(userId) {
    try {
      // Try to get from Redis cache first
      const cachedCount = await cacheService.getUnreadCount(userId);
      if (cachedCount !== null) {
        return cachedCount;
      }

      // If not in cache, calculate from database
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('is_read', false);

      if (error) {
        ErrorHandler.logError('MessagingService getUnreadCount error', error);
        return 0;
      }

      const unreadCount = count || 0;
      
      // Cache the count
      await cacheService.setUnreadCount(userId, unreadCount);
      
      return unreadCount;
    } catch (error) {
      ErrorHandler.logError('MessagingService getUnreadCount exception', error);
      return 0;
    }
  }

  /**
   * Get message templates for a specific user role
   * @param {string} userRole - The role of the user ('va' or 'employer')
   * @returns {Array} - List of message templates
   */
  getMessageTemplates(userRole) {
    if (userRole === 'employer') {
      return [
        {
          id: 1,
          name: 'Interview Invitation',
          content: 'Hi, I would like to invite you for an interview for the [Job Title] position. Are you available this week for a discussion?'
        },
        {
          id: 2,
          name: 'Application Received',
          content: 'Thank you for your application. We have received it and will review it shortly. We will get back to you soon.'
        },
        {
          id: 3,
          name: 'Additional Information Request',
          content: 'Thank you for your application. Could you please provide more information about your experience with [Skill/Tool]?'
        },
        {
          id: 4,
          name: 'Job Offer',
          content: 'We are pleased to offer you the [Job Title] position. Please let me know if you would like to discuss the details.'
        }
      ];
    } else {
      return [
        {
          id: 1,
          name: 'Thank You',
          content: 'Thank you for considering my application. I am very interested in this opportunity.'
        },
        {
          id: 2,
          name: 'Interview Availability',
          content: 'I am available for an interview on [Day] at [Time]. Please let me know if this works for you.'
        },
        {
          id: 3,
          name: 'Follow Up',
          content: 'I wanted to follow up on my application for the [Job Title] position. I am still very interested in this opportunity.'
        },
        {
          id: 4,
          name: 'Portfolio Share',
          content: 'I would like to share my portfolio with you. You can view it at [Portfolio Link].'
        }
      ];
    }
  }

  /**
   * Subscribe to real-time message updates for a conversation
   * @param {string} conversationId - The conversation ID to subscribe to
   * @param {Function} callback - Callback function for new messages
   * @returns {Object} - Subscription object
   */
  subscribeToMessages(conversationId, callback) {
    if (!conversationId || !callback) {
      return null;
    }

    try {
      // Ensure conversationId is a string and validate format
      const stringConversationId = String(conversationId);
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(stringConversationId)) {
        ErrorHandler.logError('Invalid UUID format for message subscription in MessagingService', null, { conversationId: stringConversationId });
        return null;
      }
      
      const subscription = supabase
        .channel(`messages:${stringConversationId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${stringConversationId}`
          },
          (payload) => {
            callback(payload.new);
          }
        )
        .subscribe();

      return subscription;
    } catch {
      return null;
    }
  }

  /**
   * Subscribe to real-time conversation updates
   * @param {string} userId - The user ID to subscribe to conversations for
   * @param {Function} callback - Callback function for conversation updates
   * @returns {Object} - Subscription object
   */
  subscribeToConversations(userId, callback) {
    if (!userId || !callback) {
      return null;
    }

    try {
      const subscription = supabase
        .channel(`conversations:${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'conversations',
            filter: 'participant_1.eq.' + userId + ',participant_2.eq.' + userId
          },
          (payload) => {
            callback(payload);
          }
        )
        .subscribe();

      return subscription;
    } catch {
      return null;
    }
  }

  /**
   * Unsubscribe from real-time updates
   * @param {Object} subscription - The subscription object to unsubscribe
   */
  unsubscribe(subscription) {
    if (subscription) {
      try {
        supabase.removeChannel(subscription);
      } catch {
        // Silent error handling for production
      }
    }
  }

  /**
   * Invalidate cache when job applications are created/updated
   * @param {string} vaId - The ID of the VA
   * @param {string} jobId - The ID of the job
   */
  async invalidateCacheForJobApplication(vaId, jobId) {
    try {
      // Get the employer for this job
      const { data: job } = await supabase
        .from('jobs')
        .select('employer_id')
        .eq('id', jobId)
        .single();
      
      if (job?.employer_id) {
        this.invalidateRelationshipCache(vaId, job.employer_id);
      }
    } catch (error) {
      ErrorHandler.logError('Error invalidating cache for job application in MessagingService', error);
    }
  }

  /**
   * Invalidate cache when contracts are created/updated
   * @param {string} vaId - The ID of the VA
   * @param {string} employerId - The ID of the employer
   */
  invalidateCacheForContract(vaId, employerId) {
    this.invalidateRelationshipCache(vaId, employerId);
  }

  /**
   * Clean up resources (call when service is no longer needed)
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    
    // Clean up real-time service
    if (this.realtimeHandlerId) {
      realtimeRelationshipService.removeEventHandler('relationship_changed', this.realtimeHandlerId);
      this.realtimeHandlerId = null;
    }
    
    this.clearRelationshipCache();
  }
}

const messagingService = new MessagingService();
export default messagingService;
